/**
 * The Approvals queue: the list, the side panel and the bulk confirmation.
 *
 * Pure, so the ordering, the ageing and — the case that matters — two proofs
 * paying one request can be tested without rendering. Spec: DESIGN-BRIEF.md §F
 * and `vApprovals()` in docs/design/netly-pay/prototype.html.
 */
import {
  batchOf,
  diffDays,
  fmtDate,
  fmtDateTime,
  fmtShort,
  money,
  requestBalance,
  todayISO,
  type Approval,
  type NetlyPayData,
} from '../../internals/managers/NetlyPayManager';
import {initialsOf} from './members';

export type AgeTone = 'default' | 'warning' | 'danger';

/**
 * How old a proof is allowed to look.
 *
 * Amber at two days and red at three, per §F. The boundaries are inclusive on
 * the amber side: a proof sent the day before yesterday is already amber, not
 * amber only once it is stale.
 */
export function ageTone(days: number): AgeTone {
  if (days >= 3) return 'danger';
  if (days >= 2) return 'warning';
  return 'default';
}

export function ageLabel(days: number): string {
  if (days <= 0) return 'Today';
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

export interface ApprovalRow {
  id: string;
  member: string;
  initials: string;
  requestId: string;
  requestTitle: string;
  amount: string;
  method: string;
  /** 'QR' or 'SLIP' — the thumbnail's label. */
  kind: string;
  proof: string;
  paidOn: string;
  age: string;
  ageTone: AgeTone;
  /** The "DUP?" label: another proof pays the same request. */
  isDuplicate: boolean;
}

/**
 * The queue, oldest first.
 *
 * Oldest first is the design's instruction and not a default: staff work down
 * the list, and a proof sent on Saturday must not sink under Monday's.
 *
 * `isDuplicate` is derived here rather than trusted from the API's `dupOf`,
 * because a second proof for a request already in the queue is a duplicate
 * whether or not anything has flagged it yet.
 */
export function approvalQueue(
  data: NetlyPayData,
  today: string = todayISO(),
  currency = 'Rs.',
): ApprovalRow[] {
  const approvals = (data?.approvals ?? []).slice().sort((a, b) => a.submitted.localeCompare(b.submitted));
  const perRequest: Record<string, number> = {};
  approvals.forEach((a) => {
    perRequest[a.requestId] = (perRequest[a.requestId] ?? 0) + 1;
  });

  return approvals.map((a) => {
    const member = (data?.members ?? []).find((m) => m.id === a.memberId);
    const request = (data?.requests ?? []).find((r) => r.id === a.requestId);
    const age = diffDays(a.submitted, today);
    return {
      id: a.id,
      member: member?.name ?? a.memberId,
      initials: initialsOf(member?.name ?? ''),
      requestId: a.requestId,
      requestTitle: request?.title ?? '—',
      amount: money(a.amountMinor, currency),
      method: a.method,
      kind: a.method === 'LankaQR' ? 'QR' : 'SLIP',
      proof: a.proof ?? 'Proof',
      paidOn: fmtShort(a.paidOn),
      age: ageLabel(age),
      ageTone: ageTone(age),
      isDuplicate: !!a.dupOf || perRequest[a.requestId] > 1,
    };
  });
}

export function queueSummary(
  data: NetlyPayData,
  currency = 'Rs.',
): string {
  const approvals = (data?.approvals ?? []).slice().sort((a, b) => a.submitted.localeCompare(b.submitted));
  if (!approvals.length) return 'All caught up';
  const total = approvals.reduce((s, a) => s + a.amountMinor, 0);
  return `${approvals.length} ${approvals.length === 1 ? 'payment' : 'payments'} · ${money(
    total,
    currency,
  )} · oldest sent ${fmtDate(approvals[0].submitted)}`;
}

export function selectionTotal(data: NetlyPayData, ids: string[], currency = 'Rs.'): string {
  const total = (data?.approvals ?? [])
    .filter((a) => ids.includes(a.id))
    .reduce((s, a) => s + a.amountMinor, 0);
  return `${ids.length} selected · ${money(total, currency)}`;
}

// ── The side panel ──────────────────────────────────────────────────────────

export interface ApprovalPanel {
  id: string;
  member: string;
  submitted: string;
  /** "2 of 5" — where this proof sits in the queue. */
  position: string;
  proof: string;
  ref: string;
  amount: string;
  owedMinor: number;
  /** True when the claimed amount is not what the request asks for. */
  differs: boolean;
  isDuplicate: boolean;
  duplicateText: string;
  rows: {label: string; value: string; mono: boolean; warn: boolean}[];
}

export function approvalPanel(
  data: NetlyPayData,
  approvalId: string,
  currency = 'Rs.',
): ApprovalPanel | null {
  const queue = (data?.approvals ?? []).slice().sort((a, b) => a.submitted.localeCompare(b.submitted));
  const index = queue.findIndex((a) => a.id === approvalId);
  if (index < 0) return null;
  const a = queue[index];
  const member = (data?.members ?? []).find((m) => m.id === a.memberId);
  const request = (data?.requests ?? []).find((r) => r.id === a.requestId);
  const owed = request ? requestBalance(request) : 0;
  const differs = a.amountMinor !== owed;
  // Another proof for the same request, whichever of the two is open.
  const twin = queue.find((x) => x.id !== a.id && x.requestId === a.requestId);
  const alreadyPaid = !!request && requestBalance(request) === 0;

  return {
    id: a.id,
    member: member?.name ?? a.memberId,
    submitted: fmtDateTime(a.submitted),
    position: `${index + 1} of ${queue.length}`,
    proof: a.proof ?? 'Proof',
    ref: a.ref ?? '',
    amount: money(a.amountMinor, currency),
    owedMinor: owed,
    differs,
    isDuplicate: !!twin || !!a.dupOf,
    duplicateText: alreadyPaid
      ? 'This request is already paid. Check the bank before approving again.'
      : twin
      ? `Another proof with reference ${twin.ref || a.ref} was sent for the same request.`
      : 'Another proof was sent for the same request.',
    rows: [
      {label: 'Request', value: `${a.requestId} · ${request?.title ?? '—'}`, mono: false, warn: false},
      {label: 'Requested', value: money(owed, currency), mono: true, warn: false},
      {label: 'Paid', value: money(a.amountMinor, currency), mono: true, warn: differs},
      {label: 'Method', value: a.method, mono: false, warn: false},
      {label: 'Reference', value: a.ref ?? '—', mono: true, warn: false},
      {label: 'Paid on', value: fmtDate(a.paidOn), mono: false, warn: false},
      {
        label: 'Member',
        value: member ? `${member.id} · ${batchOf(data, member.batchId)?.name ?? '—'}` : a.memberId,
        mono: false,
        warn: false,
      },
    ],
  };
}

/**
 * What approving a different amount will do.
 *
 * Less than owed leaves the rest open and the request reads Partly paid; more
 * is held as credit on the payer rather than refunded here (D48). The note is
 * shown before the button is pressed, because neither outcome is reversible
 * from this screen.
 */
export function differentAmountNote(
  amountMinor: number,
  owedMinor: number,
  currency = 'Rs.',
): string {
  if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
    return 'Enter the amount that reached your account.';
  }
  if (amountMinor < owedMinor) {
    return `${money(owedMinor - amountMinor, currency)} stays open on the request as Partly paid.`;
  }
  if (amountMinor > owedMinor) {
    return `${money(amountMinor - owedMinor, currency)} more than requested is kept as credit.`;
  }
  return 'Matches the request.';
}

export const REJECT_REASONS = [
  "Amount doesn't match",
  'Proof is unclear',
  'Duplicate',
  'Paid to wrong account',
  'Other',
];

/** The reason sent to the API, and shown to the member: chip plus any note. */
export function rejectReason(chip: string | null, note: string): string {
  if (!chip) return '';
  const extra = note.trim();
  return extra ? `${chip}. ${extra}` : chip;
}

// ── Bulk approve ────────────────────────────────────────────────────────────

export interface BulkLine {
  id: string;
  member: string;
  requestId: string;
  method: string;
  amount: string;
  /** 'Skipped' when an earlier line in this batch already pays that request. */
  flag: string;
}

export interface BulkPlan {
  title: string;
  lines: BulkLine[];
  total: string;
  totalMinor: number;
  /** True when at least one line is skipped — the confirmation says so. */
  hasDuplicate: boolean;
  duplicateNote: string;
  cta: string;
  /** The ids that will actually be approved, in queue order. */
  approveIds: string[];
}

/**
 * The bulk confirmation, line by line.
 *
 * Where two selected proofs pay the same request only the FIRST is approved —
 * the queue is oldest first, so the first is the one that arrived first — and
 * the total counts it once. Summing the selection would promise the owner
 * money that will not be banked, which is why the total is built from the
 * approved lines rather than from the ticks.
 */
export function bulkApprovePlan(
  data: NetlyPayData,
  ids: string[],
  currency = 'Rs.',
): BulkPlan {
  const queue = (data?.approvals ?? []).slice().sort((a, b) => a.submitted.localeCompare(b.submitted));
  const selected = queue.filter((a) => ids.includes(a.id));

  const seen = new Set<string>();
  const lines: BulkLine[] = [];
  const approveIds: string[] = [];
  let totalMinor = 0;

  selected.forEach((a: Approval) => {
    const skipped = seen.has(a.requestId);
    if (!skipped) {
      seen.add(a.requestId);
      approveIds.push(a.id);
      totalMinor += a.amountMinor;
    }
    const member = (data?.members ?? []).find((m) => m.id === a.memberId);
    lines.push({
      id: a.id,
      member: member?.name ?? a.memberId,
      requestId: a.requestId,
      method: a.method,
      amount: money(a.amountMinor, currency),
      flag: skipped ? 'Skipped' : '',
    });
  });

  const skippedCount = lines.length - approveIds.length;
  return {
    title: `Approve ${selected.length} ${selected.length === 1 ? 'payment' : 'payments'}?`,
    lines,
    total: money(totalMinor, currency),
    totalMinor,
    hasDuplicate: skippedCount > 0,
    duplicateNote: skippedCount
      ? `${skippedCount} ${
          skippedCount === 1 ? 'proof pays' : 'proofs pay'
        } a request another selected proof already covers. Only the first is approved.`
      : '',
    cta: `Approve ${money(totalMinor, currency)}`,
    approveIds,
  };
}
